import { createGlobalStore } from 'hox';
import { useState } from 'react';
import {
  App,
  App2Service,
  PolicyType,
  PolicyType2Service,
  Service,
} from '@Types/app';
import {
  ProvisionableEntityInfo,
  ProvisionEntity,
  ProvisionEntity2Infos,
} from '@Types/provision-entity';

type ProvisionableInput = App | Service | PolicyType;

function useProvisionEntityInfoStore() {
  const [provisionEntityInfo, setProvisionEntityInfo] =
    useState<ProvisionableEntityInfo | null>(null);

  function resolveProvisionableEntity(
    input: ProvisionableInput
  ): ProvisionEntity {
    if (input in App2Service) {
      return App2Service[input as App];
    }
    if (input in PolicyType2Service) {
      return PolicyType2Service[input as PolicyType];
    }
    return input as ProvisionEntity;
  }

  function getProvisionEntityInfo(input: ProvisionableInput) {
    const entity = resolveProvisionableEntity(input);
    const info = ProvisionEntity2Infos[entity];
    if (!info) {
      throw new Error(`No provisionable entity found for ${input}`);
    }
    setProvisionEntityInfo(info);
    return info;
  }

  function getOrderedProvisionApps(input: ProvisionableInput) {
    return getProvisionEntityInfo(input).orderedProvisionApps ?? [];
  }

  function getPrimaryApp(input: ProvisionableInput) {
    return getProvisionEntityInfo(input).primaryApp ?? null;
  }

  function getPolicyType(input: ProvisionableInput) {
    return getProvisionEntityInfo(input).policyType ?? null;
  }

  function getDelegatorAccountType(input: ProvisionableInput) {
    return getProvisionEntityInfo(input).delegatorAccountType ?? null;
  }

  function getCompanyPlanType(input: ProvisionableInput) {
    return getProvisionEntityInfo(input).companyPlanType ?? null;
  }

  return {
    provisionEntityInfo,
    getProvisionEntityInfo,
    setProvisionEntityInfo,
    getOrderedProvisionApps,
    getPrimaryApp,
    getPolicyType,
    getDelegatorAccountType,
    getCompanyPlanType,
  };
}

export const [useProvisionEntityInfo] = createGlobalStore(
  useProvisionEntityInfoStore
);
